import { type ReactNode, useCallback } from 'react'
import { PermissionCheckWrapper } from 'app/components/permissions'
import { type EntityId, type CloserSettings, isEmpty, isNotEmpty, isDefined } from '@terros/common'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { useForm } from '@mantine/form'
import { Tooltip, Input, NumberInput, Group, Stack } from '@mantine/core'
import { useToastError, useQueryDisclosure } from '@hooks'
import {
  useTranslation,
  PREFERENCE_DEFINITIONS,
  usePreferenceDetail,
  useUpdatePreference,
  useUserBulkUpdate,
  EMPTY_TIMES,
  getGranularity,
  hasInvalidSelections,
  filterValidTimeslots,
  type TimeslotFormValues,
} from '@front/model'
import { PreferenceSkeleton } from '../PreferenceSkeleton'
import { HistoryButton } from '../HistoryButton'
import { useMatchesPreferenceFilter, usePreferenceContext } from '../Context'
import { DrawerForm, TAlert, TDrawer, TDrawerBottomActions } from '../../terros'
import { ButtonReset } from '../../buttons/reset'
import { TButton, ButtonCancel, ButtonSave } from '../../buttons'
import { TimeslotsList } from './TimeslotsList'
import { GranularitySelector } from './GranularitySelector'

const PREFERENCE_KEY = 'closerSettings'
const DEFINITION = PREFERENCE_DEFINITIONS[PREFERENCE_KEY]
export const PreferenceDefaultCloserAvailability = ({ targetId }: { targetId: EntityId }): ReactNode => {
  const { t } = useTranslation()
  const { isAllowedUpdate } = usePreferenceContext()
  const label = t(DEFINITION.label)
  const description = t(DEFINITION.description)
  const matchesSearch = useMatchesPreferenceFilter(PREFERENCE_KEY)
  const {
    data: closerSettings,
    isLoading: isLoadingCloserSettings,
    error: errorCloserSettings,
  } = usePreferenceDetail(PREFERENCE_KEY, targetId)
  const {
    data: meetingDuration,
    isLoading: isLoadingMeetingDuration,
    error: errorMeetingDuration,
  } = usePreferenceDetail('meetingDuration', targetId)

  const { update, isUpdating, error: updateError } = useUpdatePreference(PREFERENCE_KEY, targetId)
  useToastError(updateError)

  const setDefault = useCallback(async () => {
    await update(undefined)
  }, [update])

  if (isLoadingCloserSettings || isLoadingMeetingDuration) return <PreferenceSkeleton />
  if (isDefined(errorCloserSettings))
    return (
      <TAlert variant='error' title={`Error loading preference: ${label}`}>
        {errorCloserSettings}
      </TAlert>
    )
  if (isDefined(errorMeetingDuration))
    return (
      <TAlert variant='error' title={`Error loading preference: ${label}`}>
        {errorMeetingDuration}
      </TAlert>
    )
  if (!closerSettings) return
  if (!meetingDuration) return
  if (!matchesSearch) return

  return (
    <div className='flex flex-row gap-4'>
      {isAllowedUpdate && (
        <Tooltip label='Reset to default'>
          <ButtonReset disabled={closerSettings?.current === undefined} loading={isUpdating} onClick={setDefault} />
        </Tooltip>
      )}
      <Input.Wrapper description={description} label={label} flex={1}>
        <Group gap='sm'>
          <EditEntityTimeslots
            targetId={targetId}
            initialSettings={closerSettings?.current ?? closerSettings?.inherited}
            meetingDuration={meetingDuration.current ?? meetingDuration.inherited}
          />
          <PermissionCheckWrapper targetId={targetId} permissionType='availability:update'>
            <ClearEntityDownlineCloserSettings targetId={targetId} />
          </PermissionCheckWrapper>
        </Group>
      </Input.Wrapper>
      <HistoryButton data={closerSettings} preferenceId={PREFERENCE_KEY} />
    </div>
  )
}

type EditEntityTimeslotsProps = {
  targetId: EntityId
  initialSettings: CloserSettings | undefined
  meetingDuration: number | undefined
}
const EditEntityTimeslots = ({
  targetId,
  initialSettings,
  meetingDuration: initialMeetingDuration,
}: EditEntityTimeslotsProps): ReactNode => {
  const { t } = useTranslation()
  const [opened, { open, close }] = useQueryDisclosure('editTimeslots')

  return (
    <>
      <TButton variant='gray' onClick={open}>
        {t('Edit Timeslots')}
      </TButton>

      <TDrawer opened={opened} onClose={close} title={t('Edit Timeslots')} size='xl'>
        {opened && (
          <TimeslotsForm
            onClose={close}
            targetId={targetId}
            initialSettings={initialSettings}
            meetingDuration={initialMeetingDuration}
          />
        )}
      </TDrawer>
    </>
  )
}

const TimeslotsForm = ({
  targetId,
  initialSettings,
  meetingDuration: initialMeetingDuration,
  onClose,
}: EditEntityTimeslotsProps & { onClose: VoidFunction }): ReactNode => {
  const { isAllowedUpdate } = usePreferenceContext()
  const { t } = useTranslation()
  const { update: updateMeetingDuration, isUpdating: isUpdatingMeetingDuration } = useUpdatePreference(
    'meetingDuration',
    targetId
  )
  const { update: updateCloserSettings, isUpdating: isUpdatingCloserSettings } = useUpdatePreference(
    'closerSettings',
    targetId
  )

  const form = useForm<TimeslotFormValues>({
    initialValues: {
      meetingDuration: initialMeetingDuration ?? 60,
      granularity: getGranularity(initialSettings?.timeslots),
      timeslots: initialSettings?.timeslots ?? EMPTY_TIMES,
    },
  })

  const handleGranularityChange = useCallback(
    (newGranularity: number): void => {
      if (newGranularity !== form.values.granularity) {
        if (hasInvalidSelections(form.values.timeslots, newGranularity)) {
          const filteredTimeslots = filterValidTimeslots(form.values.timeslots, newGranularity)

          modals.openConfirmModal({
            title: t('Change Granularity'),
            children: t('Some timeslots are not compatible with the new granularity and will be removed. Continue?'),
            labels: { confirm: t('Yes, change'), cancel: t('Cancel') },
            onConfirm: () => {
              form.setValues({
                ...form.values,
                granularity: newGranularity,
                timeslots: filteredTimeslots,
              })
            },
          })
        } else {
          form.setValues({
            ...form.values,
            granularity: newGranularity,
          })
        }
      }
    },
    [form, t]
  )

  const handleSave = useCallback(async (): Promise<void> => {
    const promises: Promise<any>[] = []

    if (form.values.meetingDuration !== initialMeetingDuration) {
      promises.push(updateMeetingDuration(form.values.meetingDuration))
    }

    promises.push(
      updateCloserSettings({
        ...initialSettings,
        timeslots: form.values.timeslots,
      })
    )

    await Promise.all(promises)

    notifications.show({
      message: t('Timeslots saved successfully'),
      color: 'green',
    })

    onClose()
  }, [form, t, initialMeetingDuration, initialSettings, updateMeetingDuration, updateCloserSettings, onClose])

  const isUpdating = isUpdatingMeetingDuration || isUpdatingCloserSettings

  return (
    <DrawerForm onSubmit={form.onSubmit(handleSave)}>
      <Stack>
        {isEmpty(form.values.meetingDuration) && (
          <TAlert variant='warning'>{t('Set a meeting duration to choose timeslots')}</TAlert>
        )}

        <Group>
          <NumberInput
            label={t('Meeting Duration')}
            description={t('Length of each appointment')}
            min={15}
            max={180}
            step={15}
            suffix=' mins'
            flex={1}
            disabled={!isAllowedUpdate}
            {...form.getInputProps('meetingDuration')}
          />

          <GranularitySelector
            value={form.values.granularity}
            onChange={handleGranularityChange}
            disabled={!form.values.meetingDuration || !isAllowedUpdate}
          />
        </Group>

        {isNotEmpty(form.values.meetingDuration) && <TimeslotsList form={form} />}
      </Stack>
      {isAllowedUpdate && (
        <TDrawerBottomActions>
          <Group justify='end' gap='md'>
            <ButtonCancel onClick={onClose} />
            <ButtonSave onClick={handleSave} loading={isUpdating} disabled={!form.isDirty()} />
          </Group>
        </TDrawerBottomActions>
      )}
    </DrawerForm>
  )
}

const ClearEntityDownlineCloserSettings = ({ targetId }: { targetId: EntityId }): ReactNode => {
  const { t } = useTranslation()
  const { isAllowedUpdate } = usePreferenceContext()
  const { update, isUpdating, error } = useUserBulkUpdate()
  useToastError(error)

  const clearDownline = useCallback(async (): Promise<void> => {
    modals.openConfirmModal({
      title: t('Clear Downline Closer Settings'),
      children: t(
        'This will remove all custom closer availability settings for users in this team and its sub-teams. Users will inherit settings from company or parent teams settings. Continue?'
      ),
      labels: { confirm: t('Yes, clear'), cancel: t('Cancel') },
      onConfirm: async () => {
        const result = await update({
          entityIds: [targetId],
          operation: { type: 'invalidateCloserSettings' },
        })
        notifications.show({
          message: t('Cleared closer settings for {{count}} users', { count: result.updatedCount }),
          color: 'green',
        })
      },
    })
  }, [targetId, update, t])

  return (
    <TButton variant='gray' onClick={clearDownline} loading={isUpdating} disabled={!isAllowedUpdate}>
      {t('Clear Downline Closer Settings')}
    </TButton>
  )
}
